import { useState, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CompanyDocumentsCard } from '@/components/settings/CompanyDocumentsCard';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Loader2, Globe } from 'lucide-react';
import { Loader } from '@/components/ui/loader';
import { ProfileImageUpload } from '@/components/settings/ProfileImageUpload';
import { SupportSettingsTab } from '@/components/settings/SupportSettingsTab';
import { EmailTemplatesTab } from '@/components/settings/EmailTemplatesTab';
import { isValidIban, normalizeIban, formatIban } from '@/lib/validations/iban';

const languages = [
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
];

const Settings = () => {
  const { user } = useAuth();
  const { i18n, t } = useTranslation();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('profile');

  // Schemas de validation
  const profileSchema = z.object({
    first_name: z.string().min(1, t('settings.validation.firstNameRequired')).max(100, t('settings.validation.maxLength', { max: 100 })),
    last_name: z.string().min(1, t('settings.validation.lastNameRequired')).max(100, t('settings.validation.maxLength', { max: 100 })),
    phone: z.string().optional(),
  });

  const companySchema = z.object({
    name: z.string().min(1, t('settings.validation.companyNameRequired')).max(200, t('settings.validation.maxLength', { max: 200 })),
    siret: z.string().optional(),
    iban: z.string().optional().refine((v) => !v || isValidIban(v), {
      message: t('settings.company.ibanInvalid'),
    }),
    address: z.string().optional(),
    phone: z.string().optional(),
  });

  const emailSchema = z.object({
    email: z.string().email(t('settings.validation.emailInvalid')),
    email_notifications_enabled: z.boolean(),
  });

  const passwordSchema = z.object({
    current_password: z.string().min(6, t('settings.validation.passwordMin')),
    new_password: z.string().min(6, t('settings.validation.passwordMin')),
    confirm_password: z.string().min(6, t('settings.validation.passwordMin')),
  }).refine((data) => data.new_password === data.confirm_password, {
    message: t('settings.validation.passwordMismatch'),
    path: ['confirm_password'],
  });

  type ProfileFormData = z.infer<typeof profileSchema>;
  type CompanyFormData = z.infer<typeof companySchema>;
  type EmailFormData = z.infer<typeof emailSchema>;
  type PasswordFormData = z.infer<typeof passwordSchema>;

  // Fetch user profile
  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['user-profile', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('users')
        .select('*, companies(*)')
        .eq('id', user?.id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  // Profile form
  const profileForm = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    values: profile ? {
      first_name: profile.first_name || '',
      last_name: profile.last_name || '',
      phone: profile.phone || '',
    } : undefined,
  });

  // Company form
  const companyForm = useForm<CompanyFormData>({
    resolver: zodResolver(companySchema),
    values: profile?.companies ? {
      name: profile.companies.name || '',
      siret: profile.companies.siret || '',
      iban: profile.companies.iban || '',
      address: profile.companies.address || '',
      phone: profile.companies.phone || '',
    } : undefined,
  });

  // Email form
  const emailForm = useForm<EmailFormData>({
    resolver: zodResolver(emailSchema),
    values: profile ? {
      email: user?.email || '',
      email_notifications_enabled: profile.email_notifications_enabled ?? true,
    } : undefined,
  });

  // Password form
  const passwordForm = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      current_password: '',
      new_password: '',
      confirm_password: '',
    },
  });

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: async (data: ProfileFormData) => {
      const { error } = await supabase
        .from('users')
        .update({
          first_name: data.first_name,
          last_name: data.last_name,
          phone: data.phone,
        })
        .eq('id', user?.id);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(t('settings.messages.profileUpdated'));
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
    },
    onError: (error) => {
      toast.error(t('settings.messages.profileError') + ': ' + error.message);
    },
  });

  // Update company mutation
  const updateCompanyMutation = useMutation({
    mutationFn: async (data: CompanyFormData) => {
      const { error } = await supabase
        .from('companies')
        .update({
          name: data.name,
          siret: data.siret,
          iban: data.iban ? normalizeIban(data.iban) : null,
          address: data.address,
          phone: data.phone,
        })
        .eq('id', profile?.company_id);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(t('settings.messages.companyUpdated'));
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
    },
    onError: (error) => {
      toast.error(t('settings.messages.companyError') + ': ' + error.message);
    },
  });

  // Update email mutation
  const updateEmailMutation = useMutation({
    mutationFn: async (data: EmailFormData) => {
      // Update email notifications preference first
      const { error: updateError } = await supabase
        .from('users')
        .update({ email_notifications_enabled: data.email_notifications_enabled })
        .eq('id', user?.id);

      if (updateError) throw updateError;

      // Only update email in auth if it changed (sent via Resend / info@myvelox.eu)
      if (data.email !== user?.email) {
        const { error: authError } = await supabase.functions.invoke('send-email-change', {
          body: { new_email: data.email, redirect_to: `${window.location.origin}/settings` },
        });
        if (authError) throw authError;
      }
    },
    onSuccess: (_, variables) => {
      if (variables.email !== user?.email) {
        toast.success(t('settings.messages.emailSent'));
      } else {
        toast.success(t('settings.messages.emailPreferencesUpdated'));
      }
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
    },
    onError: (error) => {
      toast.error(t('settings.messages.emailError') + ': ' + error.message);
    },
  });

  // Update password mutation
  const updatePasswordMutation = useMutation({
    mutationFn: async (data: PasswordFormData) => {
      // Verify current password by signing in
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user?.email || '',
        password: data.current_password,
      });

      if (signInError) {
        throw new Error(t('settings.messages.currentPasswordIncorrect'));
      }

      // Update password
      const { error } = await supabase.auth.updateUser({
        password: data.new_password,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(t('settings.messages.passwordUpdated'));
      passwordForm.reset();
    },
    onError: (error) => {
      toast.error(t('settings.messages.passwordError') + ': ' + error.message);
    },
  });

  // Update language mutation
  const updateLanguageMutation = useMutation({
    mutationFn: async (newLanguage: string) => {
      const { error } = await supabase.rpc('update_user_language', {
        new_language: newLanguage,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(t('settings.messages.languageUpdated'));
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
    },
    onError: (error) => {
      toast.error(t('settings.messages.languageError') + ': ' + error.message);
    },
  });

  const handleLanguageChange = async (newLanguage: string) => {
    // Changer immédiatement la langue de l'interface
    i18n.changeLanguage(newLanguage);
    // Mettre à jour en base de données
    updateLanguageMutation.mutate(newLanguage);
  };

  if (profileLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-semibold text-[#37699E]">
          {t('settings.title')}
        </h1>
        <p className="text-[#5A6B7A] mt-2">
          {t('settings.subtitle')}
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className={`grid w-full grid-cols-2 ${user?.role === 'admin' ? 'lg:grid-cols-7' : 'lg:grid-cols-5'}`}>
          <TabsTrigger value="profile">{t('settings.tabs.profile')}</TabsTrigger>
          <TabsTrigger value="company">{t('settings.tabs.company')}</TabsTrigger>
          <TabsTrigger value="language">{t('settings.tabs.language')}</TabsTrigger>
          <TabsTrigger value="email">{t('settings.tabs.email')}</TabsTrigger>
          <TabsTrigger value="password">{t('settings.tabs.password')}</TabsTrigger>
          {user?.role === 'admin' && (
            <TabsTrigger value="support">{t('support.title')}</TabsTrigger>
          )}
          {user?.role === 'admin' && (
            <TabsTrigger value="email-templates">{t('emailTemplates.tab')}</TabsTrigger>
          )}
        </TabsList>


        {/* Profil Tab */}
        <TabsContent value="profile">
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.profile.title')}</CardTitle>
              <CardDescription>
                {t('settings.profile.description')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ProfileImageUpload 
                avatarUrl={profile?.avatar_url} 
                firstName={profile?.first_name}
                userRole={user?.role}
              />
              <Form {...profileForm}>
                <form onSubmit={profileForm.handleSubmit((data) => updateProfileMutation.mutate(data))} className="space-y-4">
                  <FormField
                    control={profileForm.control}
                    name="first_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('settings.profile.firstName')}</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={profileForm.control}
                    name="last_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('settings.profile.lastName')}</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={profileForm.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('settings.profile.phone')}</FormLabel>
                        <FormControl>
                          <Input type="tel" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    disabled={updateProfileMutation.isPending}
                    className="bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748]"
                  >
                    {updateProfileMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {t('settings.profile.save')}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Company Tab */}
        <TabsContent value="company">
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.company.title')}</CardTitle>
              <CardDescription>
                {t('settings.company.description')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...companyForm}>
                <form onSubmit={companyForm.handleSubmit((data) => updateCompanyMutation.mutate(data))} className="space-y-4">
                  <FormField
                    control={companyForm.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('settings.company.name')}</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={companyForm.control}
                    name="siret"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('settings.company.registrationNumber')}</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder={t('settings.company.registrationNumberDescription')} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={companyForm.control}
                    name="iban"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('settings.company.iban')}</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder={t('settings.company.ibanPlaceholder')}
                            onBlur={(e) => {
                              field.onChange(e.target.value ? formatIban(e.target.value) : '');
                              field.onBlur();
                            }}
                          />
                        </FormControl>
                        <p className="text-xs text-[#5A6B7A]">{t('settings.company.ibanDescription')}</p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />


                  <FormField
                    control={companyForm.control}
                    name="address"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('settings.company.address')}</FormLabel>
                        <FormControl>
                          <Textarea {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={companyForm.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('settings.company.phone')}</FormLabel>
                        <FormControl>
                          <Input type="tel" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    disabled={updateCompanyMutation.isPending}
                    className="bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748]"
                  >
                    {updateCompanyMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {t('settings.company.save')}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>

          <CompanyDocumentsCard companyId={profile?.company_id} />
        </TabsContent>

        {/* Language Tab */}
        <TabsContent value="language">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                {t('settings.language.title')}
              </CardTitle>
              <CardDescription>
                {t('settings.language.description')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">{t('settings.language.label')}</label>
                <Select
                  value={profile?.preferred_language || i18n.language}
                  onValueChange={handleLanguageChange}
                  disabled={updateLanguageMutation.isPending}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue>
                      <div className="flex items-center gap-2">
                        <span>{languages.find(l => l.code === (profile?.preferred_language || i18n.language))?.flag}</span>
                        <span>{languages.find(l => l.code === (profile?.preferred_language || i18n.language))?.name}</span>
                      </div>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {languages.map((language) => (
                      <SelectItem key={language.code} value={language.code}>
                        <div className="flex items-center gap-2">
                          <span>{language.flag}</span>
                          <span>{language.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {t('settings.language.note')}
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email Tab */}
        <TabsContent value="email">
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.email.title')}</CardTitle>
              <CardDescription>
                {t('settings.email.description')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...emailForm}>
                <form onSubmit={emailForm.handleSubmit((data) => updateEmailMutation.mutate(data))} className="space-y-4">
                  <FormField
                    control={emailForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('settings.email.newEmail')}</FormLabel>
                        <FormControl>
                          <Input type="email" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={emailForm.control}
                    name="email_notifications_enabled"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border border-[#DBE2EA] p-4">
                        <div className="space-y-0.5">
                          <FormLabel className="text-base">
                            {t('settings.email.notifications.label')}
                          </FormLabel>
                          <p className="text-sm text-[#5A6B7A]">
                            {t('settings.email.notifications.description')}
                          </p>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    disabled={updateEmailMutation.isPending}
                    className="bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748]"
                  >
                    {updateEmailMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {t('settings.email.update')}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Password Tab */}
        <TabsContent value="password">
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.password.title')}</CardTitle>
              <CardDescription>
                {t('settings.password.description')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...passwordForm}>
                <form onSubmit={passwordForm.handleSubmit((data) => updatePasswordMutation.mutate(data))} className="space-y-4">
                  <FormField
                    control={passwordForm.control}
                    name="current_password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('settings.password.currentPassword')}</FormLabel>
                        <FormControl>
                          <Input type="password" placeholder="••••••••" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={passwordForm.control}
                    name="new_password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('settings.password.newPassword')}</FormLabel>
                        <FormControl>
                          <Input type="password" placeholder="••••••••" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={passwordForm.control}
                    name="confirm_password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('settings.password.confirmPassword')}</FormLabel>
                        <FormControl>
                          <Input type="password" placeholder="••••••••" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    disabled={updatePasswordMutation.isPending}
                    className="bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748]"
                  >
                    {updatePasswordMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {t('settings.password.save')}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>
        </TabsContent>

        {user?.role === 'admin' && (
          <TabsContent value="support">
            <SupportSettingsTab />
          </TabsContent>
        )}

        {user?.role === 'admin' && (
          <TabsContent value="email-templates">
            <EmailTemplatesTab />
          </TabsContent>
        )}
      </Tabs>
    </div>

  );
};

export default Settings;
